import { Badge, Button, Collapse, Group, Stack, Tabs, TextInput } from "@mantine/core";
import { DownloadSimpleIcon, FunnelIcon, LockIcon, MagnifyingGlassIcon, WarningIcon } from "@phosphor-icons/react";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { i18n } from "@dehub/theme";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { ReturnApi } from "../../../apis";
import type { IReturnListCounts, IReturnListParams, ReturnRequestStatus } from "../../../apis/return";
import { PageLayout } from "../../../components/layout";
import { BulkActionToolbar } from "../../../components/return/bulk-action-toolbar";
import { ReturnListFilters, ListFilterValue } from "../../../components/return/list-filters";
import { ReturnListTable } from "../../../components/return/list-table";
import { ReturnsExportModal } from "../../../components/return/export-modal";
import { ReturnModuleTabs } from "../../../components/return/module-tabs";
import { BE_MAX_DISPUTES, useReturnActions } from "../../../components/return/return-actions";
import { trustLevelLabel } from "../../../components/return/trust-level-badge";
import { TableFrame } from "../../../components/delivery/atoms";
import { ListState, ReasonTooltip } from "../../../components/ui";

// Default filter state — the list opens on a trailing-30-day window (§9.1).
const DEFAULT_FILTERS: ListFilterValue = { datePreset: "30" };

const DAY_MS = 24 * 60 * 60 * 1000;

const TAB_STATUS_MAP: Record<string, ReturnRequestStatus[]> = {
  pending  : ["REQUESTED", "PROPOSED", "DISPUTED"],
  active   : ["ACCEPTED", "AWAITING_PICKUP_SCHEDULE", "AWAITING_SHIPMENT_INFO", "AWAITING_WAREHOUSE_RECEIPT", "IN_TRANSIT", "IN_TRANSIT_EXCEPTION", "AWAITING_RESOLUTION", "REFUND_PENDING", "PARTIALLY_COMPLETED"],
  completed: ["COMPLETED"],
  cancelled: ["REJECTED", "CANCELLED"],
};

type TabKey = keyof typeof TAB_STATUS_MAP;

const TAB_KEYS: TabKey[] = ["pending", "active", "completed", "cancelled"];

function tabCount (key: TabKey, counts?: IReturnListCounts): number | undefined {
  if (!counts) return undefined;
  return TAB_STATUS_MAP[key].reduce((sum, s) => sum + (counts.byStatus?.[s] ?? 0), 0);
}

const breadcrumb = () => [
  { to: "/", label: i18n.t("returns:breadcrumb.home") },
  { to: "/returns", label: i18n.t("returns:breadcrumb.returns") },
  { label: i18n.t("returns:common.list") }
];

function buildParams(
  filters: ListFilterValue,
  page: number,
  tab: TabKey
): IReturnListParams {
  const { dateFrom, dateTo } = resolveDateRange(filters);

  return {
    status                : TAB_STATUS_MAP[tab],
    requestType           : filters.requestType ?? undefined,
    returnReason          : filters.returnReason?.length ? filters.returnReason : undefined,
    resolutionType        : filters.resolutionType ?? undefined,
    buyerBusinessId       : filters.buyerBusinessId ?? undefined,
    trustTier             : filters.trustTier?.length ? filters.trustTier : undefined,
    isConsignmentReturn   : filters.isConsignmentReturn || undefined,
    search                : filters.search || undefined,
    dateFrom,
    dateTo,
    page,
    limit                 : 20,
    sortBy                : "requestedAt",
    sortDir               : "desc"
  };
}

// Resolves the date filter to ISO from/to. Numeric presets = trailing window;
// "custom" uses the explicit pickers; "all" clears the date filter. new Date()
// is normalised so a picker returning a string (Mantine v9) is still safe.
function resolveDateRange (filters: ListFilterValue): { dateFrom?: string; dateTo?: string }{
  const preset = filters.datePreset ?? "30";
  if (preset === "all") return {};
  if (preset === "custom") {
    return {
      dateFrom: filters.dateFrom ? new Date(filters.dateFrom).toISOString() : undefined,
      dateTo  : filters.dateTo ? new Date(filters.dateTo).toISOString() : undefined
    };
  }
  const to = new Date();
  const from = new Date(to.getTime() - Number(preset) * DAY_MS);
  return { dateFrom: from.toISOString(), dateTo: to.toISOString() };
}

export function ReturnListPage() {
  const { t } = useTranslation("returns");
  const navigate = useNavigate();
  const { permissionState } = useReturnActions();
  const [filters, setFilters] = useState<ListFilterValue>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<TabKey>("pending");
  const [exportOpen, exportH] = useDisclosure(false);
  const [filtersOpen, filtersH] = useDisclosure(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // O194 — transit exceptions stay visible without RETURN_RESOLVE_TRANSIT, aria-disabled with the code.
  const transitState = permissionState("resolveTransit");
  // O04 — off-system create: RETURN_CREATE (off-system-claim.ts); the admin flow is not built yet.
  const createState = permissionState("offSystemCreate");
  const createReason = createState.reason ?? t("listPage.newRequestTooltip");

  // Reset every filter back to the 30-day default in one click (Цэвэрлэх).
  const onClearAll = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const activeFilterCount = [
    filters.returnReason?.length,
    filters.resolutionType,
    filters.buyerBusinessId,
    filters.trustTier?.length,
    filters.requestType,
    filters.datePreset && filters.datePreset !== "30" ? 1 : undefined,
    filters.isConsignmentReturn,
  ].filter(Boolean).length;

  // Debounce only the free-text search (300ms); an empty box drops the param immediately.
  const [debouncedSearch] = useDebouncedValue(filters.search ?? "", 300);
  const params = useMemo(() => {
    const effectiveSearch = filters.search ? debouncedSearch : "";
    return buildParams({ ...filters, search: effectiveSearch }, page, tab);
  }, [filters, debouncedSearch, page, tab]);

  const { data, error, isLoading, mutate } = useSWR(
    ["returns.list", params],
    () => ReturnApi.list(params),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  // Derived sum of the selected rows' amounts (current page only). No refetch.
  const { selectedTotal, selectedCurrency, selectedRows } = useMemo(() => {
    const rows = (data?.returns ?? []).filter((r) => selectedIds.includes(r.returnRequestId));
    return {
      selectedTotal   : rows.reduce((sum, r) => sum + (r.totalRequestedAmount ?? 0), 0),
      selectedCurrency: rows[0]?.currency ?? "",
      selectedRows    : rows
    };
  }, [data, selectedIds]);

  const onTabChange = (value: string | null) => {
    if (!value) return;
    setTab(value as TabKey);
    setPage(1);
    setSelectedIds([]);
  };

  // §25 export-modal filter recap (read-only summary of what will be exported).
  const exportRecap = useMemo(() => {
    const presetLabel = filters.datePreset === "all"
      ? t("filters.datePreset.all")
      : filters.datePreset === "custom"
      ? t("listPage.customRange")
      : t("listPage.lastNDays", { count: Number(filters.datePreset ?? "30") });
    const rows = [
      { label: t("filters.date"), value: presetLabel },
      { label: t("listPage.tabLabel"), value: t(`listPage.tab.${tab}`) }
    ];
    if (filters.search) rows.push({ label: t("listPage.searchLabel"), value: filters.search });
    if (filters.returnReason?.length) rows.push({ label: t("filters.reasonLabel"), value: t("listPage.nSelected", { count: filters.returnReason.length }) });
    if (filters.trustTier?.length) rows.push({ label: t("listTable.col.trust"), value: filters.trustTier.map((x) => trustLevelLabel(x)).join(", ") });
    return rows;
  }, [filters, tab, t]);

  return (
    <PageLayout
      title={t("breadcrumb.returns")}
      subTitle={t("listPage.subtitle")}
      breadcrumb={breadcrumb()}
      extra={[
        <ReasonTooltip key="transit" reason={transitState.reason}>
          <Button
            variant="default"
            leftSection={<WarningIcon size={18} />}
            onClick={() => navigate("/returns/transit-exceptions")}
          >
            {t("transitExceptions.title")}
          </Button>
        </ReasonTooltip>,
        <Button key="export" variant="default" leftSection={<DownloadSimpleIcon size={18} />} onClick={exportH.open}>
          {t("listPage.export")}
        </Button>,
        <ReasonTooltip key="new" reason={createReason}>
          <Button variant="light" color="gray" leftSection={<LockIcon size={18} />}>
            {t("listPage.newRequest")}
          </Button>
        </ReasonTooltip>,
      ]}
    >
      <Stack gap="md">
        <ReturnModuleTabs active="list" />

        <Tabs value={tab} onChange={onTabChange} aria-label={t("listPage.tabsAria")}>
          <Tabs.List>
            {TAB_KEYS.map((key) => {
              const cnt = tabCount(key, data?.counts);
              return (
                <Tabs.Tab
                  key={key}
                  value={key}
                  rightSection={cnt !== undefined ? <Badge size="md" variant="light" color="gray" radius="xl">{cnt}</Badge> : undefined}
                >
                  {t(`listPage.tab.${key}`)}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        </Tabs>

        <Stack gap="sm">
          <Group justify="space-between" align="flex-end" wrap="nowrap" gap="sm">
            <TextInput
              placeholder={t("filters.searchPlaceholder")}
              aria-label={t("listPage.searchLabel")}
              leftSection={<MagnifyingGlassIcon size={16} />}
              value={filters.search ?? ""}
              onChange={(e) => {
                setFilters({ ...filters, search: e.currentTarget.value });
                setPage(1);
              }}
              w={420}
              maw="100%"
            />
            <Button
              variant="default"
              leftSection={<FunnelIcon size={16} />}
              rightSection={activeFilterCount > 0 ? <Badge size="md" variant="light" color="gray" radius="xl">{activeFilterCount}</Badge> : undefined}
              onClick={filtersH.toggle}
              aria-expanded={filtersOpen}
            >
              {t("export.filters")}
            </Button>
          </Group>
          <Collapse expanded={filtersOpen}>
            <ReturnListFilters
              value={filters}
              onChange={(next) => {
                setFilters(next);
                setPage(1);
              }}
              onClear={onClearAll}
              showSearch={false}
            />
          </Collapse>
        </Stack>

        <Stack gap="md">
          <BulkActionToolbar
            selectedIds={selectedIds}
            selectedTotal={selectedTotal}
            currency={selectedCurrency}
            selectedRows={selectedRows}
            onClearSelection={() => setSelectedIds([])}
            onMutate={() => mutate()}
          />
          {error && !data ? (
            <TableFrame>
              <ListState state="error" onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
            </TableFrame>
          ) : (
            <ReturnListTable
              rows={data?.returns ?? []}
              page={data?.pagination?.page ?? page}
              totalPages={data?.pagination?.totalPages ?? 1}
              total={data?.pagination?.total}
              limit={data?.pagination?.limit}
              disputeMax={BE_MAX_DISPUTES}
              onPageChange={setPage}
              loading={isLoading}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              emptyTitle={activeFilterCount > 0 || filters.search ? t("listTable.noResults") : t("listTable.empty")}
              emptyDescription={activeFilterCount > 0 || filters.search ? t("listTable.noResultsHint") : t("listTable.emptyHint")}
            />
          )}
        </Stack>
      </Stack>

      <ReturnsExportModal
        opened={exportOpen}
        onClose={exportH.close}
        params={params}
        recap={exportRecap}
      />
    </PageLayout>
  );
}
